import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Socket } from "socket.io-client";
import { QK } from "./queryKeys";
import { chatApi } from "./chatApi";
import type { UnreadSummary } from "./chatApi";

/**
 * Returns the global unread summary. When the socket is connected, we rely
 * on `chat:message:new` events incrementing the counter and disable the
 * polling refetch interval.
 */
export function useUnreadSummary(opts?: { isSocketConnected?: boolean }) {
  const isSocketConnected = opts?.isSocketConnected ?? false;
  return useQuery({
    queryKey: QK.unreadSummary(),
    queryFn: () => chatApi.unreadSummary(),
    refetchInterval: isSocketConnected ? false : 30_000,
    staleTime: isSocketConnected ? Infinity : 10_000,
  });
}

export function useMarkRead(socket: Socket | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["community_chat", "markRead"],
    mutationFn: ({
      conversationId,
      lastReadMessageId,
    }: {
      conversationId: string;
      lastReadMessageId: string;
    }) => chatApi.markRead(conversationId, lastReadMessageId),
    onMutate: async ({ conversationId }) => {
      await qc.cancelQueries({ queryKey: QK.unreadSummary() });
      const previous = qc.getQueryData<UnreadSummary>(QK.unreadSummary());
      qc.setQueryData<UnreadSummary | undefined>(
        QK.unreadSummary(),
        (prev) => {
          if (!prev) return prev;
          const removed = prev.perConversation[conversationId] ?? 0;
          const { [conversationId]: _omit, ...rest } = prev.perConversation;
          void _omit;
          return {
            totalUnread: Math.max(0, prev.totalUnread - removed),
            perConversation: rest,
          };
        },
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(QK.unreadSummary(), ctx.previous);
      }
    },
    onSuccess: (_data, vars) => {
      if (socket && socket.connected) {
        socket.emit("chat:read", {
          conversationId: vars.conversationId,
          lastMessageId: vars.lastReadMessageId,
        });
      }
    },
  });
}
